/**
 * dsp.js — shared synthesis primitives.
 *
 * Everything in OPERATION BLACKOUT's soundscape is generated by the Web Audio API
 * at runtime; there is not a single byte of sample data in the repo. This module is
 * the vocabulary every sound family is written in: noise generators, percussive
 * envelopes, filter/waveshaper helpers, an offline batch renderer, and a small
 * measurement suite (peak/RMS/decay/spectral centroid) used by the test harness.
 *
 * Conventions
 * -----------
 * - A "synth function" has the signature `(ac, dest, t, rng, cfg) => void`. It builds
 *   a node graph that renders one instance of a sound into `dest`, starting at the
 *   absolute context time `t`. It must schedule a `stop()` on every source it makes.
 * - `rng` is a seeded generator so a rebuild is bit-identical; per-shot randomness at
 *   play time comes from the playback layer, not from re-synthesis.
 * - All sources are mono. Spatialisation happens later, in the PannerNode.
 */

/* ------------------------------------------------------------------ random --- */

/** mulberry32 — small, fast, decent distribution, fully deterministic. */
export function makeRng(seed = 1) {
  let a = seed >>> 0;
  return function rng() {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export const rand = (rng, a, b) => a + (b - a) * rng();
export const randSign = (rng) => (rng() < 0.5 ? -1 : 1);
export const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
export const lerp = (a, b, t) => a + (b - a) * t;
/** Multiplier for a pitch offset in semitones. */
export const semis = (n) => Math.pow(2, n / 12);
/** Multiplier for a level offset in decibels. */
export const dB = (n) => Math.pow(10, n / 20);

/* ------------------------------------------------------------------- noise --- */

const NOISE_SECONDS = 3;

function fillWhite(d, rng) {
  for (let i = 0; i < d.length; i++) d[i] = rng() * 2 - 1;
}

/** Paul Kellet's economical pink filter — 1/f slope, the texture of most real noise. */
function fillPink(d, rng) {
  let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
  for (let i = 0; i < d.length; i++) {
    const w = rng() * 2 - 1;
    b0 = 0.99886 * b0 + w * 0.0555179;
    b1 = 0.99332 * b1 + w * 0.0750759;
    b2 = 0.969 * b2 + w * 0.153852;
    b3 = 0.8665 * b3 + w * 0.3104856;
    b4 = 0.55 * b4 + w * 0.5329522;
    b5 = -0.7616 * b5 - w * 0.016898;
    d[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.2;
    b6 = w * 0.115926;
  }
}

/** 1/f^2 — the rumble under explosions and heavy landings. */
function fillBrown(d, rng) {
  let last = 0;
  for (let i = 0; i < d.length; i++) {
    const w = rng() * 2 - 1;
    last = (last + 0.02 * w) / 1.02;
    d[i] = last * 3.5;
  }
}

/**
 * Noise buffers are expensive to make and identical between uses, so each context
 * keeps exactly one of each colour and every source loops it from a random offset.
 */
export function getNoise(ac, type = 'white') {
  const key = `__noise_${type}`;
  if (!ac[key]) {
    const b = ac.createBuffer(1, Math.floor(NOISE_SECONDS * ac.sampleRate), ac.sampleRate);
    const d = b.getChannelData(0);
    const rng = makeRng(type === 'white' ? 0x51ed : type === 'pink' ? 0x9e37 : 0x2545);
    if (type === 'pink') fillPink(d, rng);
    else if (type === 'brown') fillBrown(d, rng);
    else fillWhite(d, rng);
    ac[key] = b;
  }
  return ac[key];
}

/** A looping noise source. Caller must `start(t, offset)` and `stop(t)`. */
export function noiseSource(ac, type = 'white', rate = 1) {
  const s = ac.createBufferSource();
  s.buffer = getNoise(ac, type);
  s.loop = true;
  s.playbackRate.value = rate;
  return s;
}

/** Start a noise source at a random point in the loop so repeats never phase-match. */
export function startNoise(s, t, dur, rng) {
  s.start(t, rng ? rng() * (NOISE_SECONDS - 0.4) : 0);
  s.stop(t + dur);
}

/* --------------------------------------------------------------- envelopes --- */

const FLOOR = 1e-5;

/**
 * Percussive envelope: linear attack to `peak`, exponential decay to silence.
 * Returns the GainNode so it can be spliced into a chain.
 */
export function perc(ac, t, peak, attack = 0.001, decay = 0.1) {
  const p = Math.max(peak, FLOOR * 2);
  const g = ac.createGain();
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(p, t + attack);
  g.gain.exponentialRampToValueAtTime(FLOOR, t + attack + decay);
  g.gain.setValueAtTime(0, t + attack + decay + 0.001);
  return g;
}

/** Attack / hold / release envelope, for anything with a body rather than a hit. */
export function ahr(ac, t, peak, attack = 0.01, hold = 0.1, release = 0.2) {
  const p = Math.max(peak, FLOOR * 2);
  const g = ac.createGain();
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(p, t + attack);
  g.gain.setValueAtTime(p, t + attack + hold);
  g.gain.exponentialRampToValueAtTime(FLOOR, t + attack + hold + release);
  g.gain.setValueAtTime(0, t + attack + hold + release + 0.001);
  return g;
}

/** Envelope from an arbitrary breakpoint list `[[timeOffset, value], ...]`. */
export function shape(ac, t, points) {
  const g = ac.createGain();
  g.gain.setValueAtTime(Math.max(points[0][1], 0), t + points[0][0]);
  for (let i = 1; i < points.length; i++) {
    const [dt, v] = points[i];
    if (v <= FLOOR) g.gain.exponentialRampToValueAtTime(FLOOR, t + dt);
    else g.gain.linearRampToValueAtTime(v, t + dt);
  }
  g.gain.setValueAtTime(0, t + points[points.length - 1][0] + 0.001);
  return g;
}

/* ----------------------------------------------------------------- filters --- */

export function biquad(ac, type, freq, Q = 1, gain = 0) {
  const f = ac.createBiquadFilter();
  f.type = type;
  f.frequency.value = clamp(freq, 10, Math.min(20000, ac.sampleRate * 0.48));
  f.Q.value = Q;
  if (type === 'peaking' || type === 'lowshelf' || type === 'highshelf') f.gain.value = gain;
  return f;
}

/** Sweep a filter cutoff exponentially — the workhorse of "movement" in a sound. */
export function sweep(param, t, from, to, dur) {
  param.setValueAtTime(Math.max(from, 10), t);
  param.exponentialRampToValueAtTime(Math.max(to, 10), t + dur);
}

const SHAPER_CURVES = new Map();

/**
 * tanh saturator. Placed *after* a gain envelope so loud transients distort and
 * quiet tails stay clean, which is what gives synthesised gunfire its weight.
 */
export function waveshaper(ac, drive = 2) {
  const key = drive.toFixed(3);
  let curve = SHAPER_CURVES.get(key);
  if (!curve) {
    const n = 2048;
    curve = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      const x = (i / (n - 1)) * 2 - 1;
      curve[i] = Math.tanh(x * drive) / Math.tanh(drive);
    }
    SHAPER_CURVES.set(key, curve);
  }
  const ws = ac.createWaveShaper();
  ws.curve = curve;
  ws.oversample = '2x';
  return ws;
}

/**
 * Safety saturator for the end of the master chain.
 *
 * Exactly unity below the knee, then a smooth exponential approach to a ceiling
 * below full scale, so the output can never clip no matter what the limiter lets
 * through. The knee is C1-continuous at the threshold — a kink there would generate
 * its own harmonics on every transient.
 *
 * WaveShaper clamps inputs beyond ±1 to the curve endpoints, so the endpoint value
 * IS the hard ceiling: 0.897, leaving ~0.9 dB for the overshoot that oversampled
 * resampling can introduce.
 */
export function softClipper(ac, knee = 0.72) {
  const n = 4096;
  const curve = new Float32Array(n);
  const r = 1 - knee;
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    const a = Math.abs(x);
    curve[i] = a <= knee ? x : Math.sign(x) * (1 - r * Math.exp(-(a - knee) / r));
  }
  const ws = ac.createWaveShaper();
  ws.curve = curve;
  ws.oversample = '2x';
  return ws;
}

export function gain(ac, v = 1) {
  const g = ac.createGain();
  g.gain.value = v;
  return g;
}

/** Connect a list of nodes head-to-tail. Returns the last node. */
export function chain(...nodes) {
  for (let i = 0; i < nodes.length - 1; i++) nodes[i].connect(nodes[i + 1]);
  return nodes[nodes.length - 1];
}

/* ------------------------------------------------------------ voice bricks --- */

/**
 * One decaying sinusoidal partial. Stack a handful at inharmonic ratios and you get
 * metal; stack them at harmonic ratios and you get a tuned body.
 */
export function partial(ac, dest, t, freq, level, decay, { type = 'sine', attack = 0.0008, detune = 0, glideTo = 0 } = {}) {
  const o = ac.createOscillator();
  o.type = type;
  o.frequency.value = clamp(freq, 10, ac.sampleRate * 0.45);
  if (detune) o.detune.value = detune;
  if (glideTo) o.frequency.exponentialRampToValueAtTime(clamp(glideTo, 10, ac.sampleRate * 0.45), t + decay);
  const g = perc(ac, t, level, attack, decay);
  chain(o, g, dest);
  o.start(t);
  o.stop(t + attack + decay + 0.02);
  return g;
}

/** A bank of partials: `[freqRatio, level, decay]` triples against a base frequency. */
export function modal(ac, dest, t, base, specs, { level = 1, spread = 0, rng = null, type = 'sine' } = {}) {
  for (const [ratio, lv, dec] of specs) {
    const jitter = spread && rng ? 1 + (rng() - 0.5) * spread : 1;
    partial(ac, dest, t, base * ratio * jitter, lv * level, dec, { type });
  }
}

/** Band-limited noise burst — the generic "material texture" brick. */
export function noiseBurst(ac, dest, t, rng, {
  type = 'white', freq = 1000, Q = 1, filter = 'bandpass',
  level = 1, attack = 0.001, decay = 0.1, sweepTo = 0, rate = 1,
} = {}) {
  const src = noiseSource(ac, type, rate);
  const f = biquad(ac, filter, freq, Q);
  if (sweepTo) sweep(f.frequency, t, freq, sweepTo, attack + decay);
  const g = perc(ac, t, level, attack, decay);
  chain(src, f, g, dest);
  startNoise(src, t, attack + decay + 0.03, rng);
  return g;
}

/**
 * Granular texture: many tiny filtered noise grains scattered over a window. This is
 * what makes gravel and sand read as *material* rather than as a pitch-shifted slap —
 * the ear hears the individual particle transients, not a single envelope.
 */
export function grains(ac, dest, t, rng, {
  count = 10, window: win = 0.07, loFreq = 500, hiFreq = 3000, Q = 3,
  level = 1, grainDecay = 0.012, type = 'white', decayShape = 2.2,
} = {}) {
  for (let i = 0; i < count; i++) {
    const u = i / Math.max(1, count - 1);
    const at = t + Math.pow(rng(), 0.7) * win;
    const env = Math.pow(1 - u, decayShape) * rand(rng, 0.45, 1);
    const f = loFreq * Math.pow(hiFreq / loFreq, rng());
    noiseBurst(ac, dest, at, rng, {
      type, freq: f, Q: Q * rand(rng, 0.7, 1.4), level: level * env,
      attack: 0.0004, decay: grainDecay * rand(rng, 0.5, 1.8),
    });
  }
}

/* --------------------------------------------------- offline batch renderer --- */

/**
 * Render `count` variations of a sound in a single OfflineAudioContext by laying them
 * out in adjacent time slots, then slicing the result.
 *
 * Creating an OfflineAudioContext costs far more than the rendering itself, so
 * batching cuts construction time by roughly an order of magnitude versus one
 * context per variation.
 */
export async function renderSlots(sampleRate, count, slot, build, { hp = 18 } = {}) {
  const n = Math.max(64, Math.floor(slot * sampleRate));
  const oac = new OfflineAudioContext(1, n * count, sampleRate);
  // A gentle highpass on the render bus guarantees zero DC offset in every buffer.
  const bus = biquad(oac, 'highpass', hp, 0.5);
  bus.connect(oac.destination);
  for (let i = 0; i < count; i++) build(oac, bus, (i * n) / sampleRate, i);
  const rendered = await oac.startRendering();
  const src = rendered.getChannelData(0);
  const out = [];
  for (let i = 0; i < count; i++) {
    const b = oac.createBuffer(1, n, sampleRate);
    b.getChannelData(0).set(src.subarray(i * n, i * n + n));
    out.push(b);
  }
  return out;
}

/**
 * Post-process a family of buffers.
 * - Removes residual DC.
 * - Trims trailing silence (so a 1.3 s slot holding a 0.4 s sound does not cost 1.3 s
 *   of memory or voice lifetime) and applies a short cosine fade so the buffer can
 *   never end on a non-zero sample — an abrupt end is an audible click.
 * - Normalises.
 *
 * `mode: 'each'` (the default) peak-normalises every variation independently. That
 * matters more than it sounds: a sub-millisecond noise transient has a *random* peak,
 * so under family normalisation one lucky variation sets the scale and its siblings
 * end up 7 dB quieter with completely different envelope shapes. Levelling each one
 * and letting the playback layer apply a controlled ±1 dB gives variety that is
 * chosen rather than accidental.
 *
 * `mode: 'family'` keeps the relative levels — right when the variations genuinely
 * differ in size (a shell that bounces twice versus three times).
 */
export function finishBuffers(ac, buffers, { peak = 0.9, fadeMs = 7, trimDb = -68, mode = 'each' } = {}) {
  const peaks = buffers.map((b) => {
    const d = b.getChannelData(0);
    let sum = 0;
    for (let i = 0; i < d.length; i++) sum += d[i];
    const dc = sum / d.length;
    let m = 0;
    for (let i = 0; i < d.length; i++) {
      d[i] -= dc;
      const a = Math.abs(d[i]);
      if (a > m) m = a;
    }
    return m;
  });
  const familyMax = Math.max(...peaks, 1e-6);
  const fadeN = Math.max(8, Math.floor((fadeMs / 1000) * ac.sampleRate));

  return buffers.map((b, i) => {
    const ref = mode === 'each' ? Math.max(peaks[i], 1e-6) : familyMax;
    const scale = peak / ref;
    const thresh = ref * Math.pow(10, trimDb / 20);
    const d = b.getChannelData(0);
    let end = d.length;
    while (end > 64 && Math.abs(d[end - 1]) < thresh) end--;
    end = Math.min(d.length, end + fadeN);
    const out = ac.createBuffer(1, end, b.sampleRate);
    const o = out.getChannelData(0);
    for (let j = 0; j < end; j++) o[j] = d[j] * scale;
    const f0 = Math.max(0, end - fadeN);
    for (let j = f0; j < end; j++) {
      const u = (j - f0) / (end - f0);
      o[j] *= 0.5 + 0.5 * Math.cos(Math.PI * u);
    }
    return out;
  });
}

/* ------------------------------------------------------------- measurement --- */

/** In-place iterative radix-2 FFT. `re`/`im` length must be a power of two. */
export function fft(re, im) {
  const n = re.length;
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      let t = re[i]; re[i] = re[j]; re[j] = t;
      t = im[i]; im[i] = im[j]; im[j] = t;
    }
  }
  for (let len = 2; len <= n; len <<= 1) {
    const ang = (-2 * Math.PI) / len;
    const wr = Math.cos(ang), wi = Math.sin(ang);
    for (let i = 0; i < n; i += len) {
      let cr = 1, ci = 0;
      for (let k = 0; k < len >> 1; k++) {
        const ar = re[i + k], ai = im[i + k];
        const br = re[i + k + (len >> 1)] * cr - im[i + k + (len >> 1)] * ci;
        const bi = re[i + k + (len >> 1)] * ci + im[i + k + (len >> 1)] * cr;
        re[i + k] = ar + br; im[i + k] = ai + bi;
        re[i + k + (len >> 1)] = ar - br; im[i + k + (len >> 1)] = ai - bi;
        const nr = cr * wr - ci * wi;
        ci = cr * wi + ci * wr;
        cr = nr;
      }
    }
  }
}

/**
 * Averaged magnitude spectrum (Welch, Hann windows, 50% overlap).
 * Returns magnitudes for bins 0..N/2 plus the bin width in Hz.
 */
export function spectrum(buffer, fftSize = 2048) {
  const d = buffer.getChannelData(0);
  const half = fftSize >> 1;
  const mag = new Float32Array(half);
  const re = new Float32Array(fftSize);
  const im = new Float32Array(fftSize);
  const win = new Float32Array(fftSize);
  for (let i = 0; i < fftSize; i++) win[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (fftSize - 1));
  const hop = fftSize >> 1;
  let frames = 0;
  for (let off = 0; off + fftSize <= d.length; off += hop) {
    for (let i = 0; i < fftSize; i++) { re[i] = d[off + i] * win[i]; im[i] = 0; }
    fft(re, im);
    for (let k = 0; k < half; k++) mag[k] += Math.hypot(re[k], im[k]);
    frames++;
  }
  if (frames === 0) {
    for (let i = 0; i < fftSize; i++) { re[i] = (d[i] || 0) * win[i]; im[i] = 0; }
    fft(re, im);
    for (let k = 0; k < half; k++) mag[k] = Math.hypot(re[k], im[k]);
    frames = 1;
  }
  for (let k = 0; k < half; k++) mag[k] /= frames;
  return { mag, binHz: buffer.sampleRate / fftSize };
}

/**
 * Numeric report for a rendered buffer. Because the synthesis is verified without
 * listening, these are the acceptance criteria: peak headroom, attack sharpness,
 * tail length, DC offset and the terminal sample (a click detector).
 */
export function analyze(buffer) {
  const d = buffer.getChannelData(0);
  const sr = buffer.sampleRate;
  let peak = 0, peakAt = 0, sumSq = 0, sum = 0;
  for (let i = 0; i < d.length; i++) {
    const a = Math.abs(d[i]);
    if (a > peak) { peak = a; peakAt = i; }
    sumSq += d[i] * d[i];
    sum += d[i];
  }
  const rms = Math.sqrt(sumSq / d.length);

  // Peak-hold envelope: instantaneous attack, 10 ms release.
  //
  // A smoothed follower is the wrong instrument here. Give it a 0.4 ms attack and a
  // 0.5 ms gunshot transient only charges it part way, so it reports the *body* as
  // the envelope peak and claims a 7 ms attack on a sound whose sample peak is at
  // 0.45 ms. Instantaneous attack measures what is actually being asked: how long
  // from onset until the waveform reaches its maximum.
  const env = new Float32Array(d.length);
  let e = 0;
  const rC = Math.exp(-1 / (0.010 * sr));
  for (let i = 0; i < d.length; i++) {
    const a = Math.abs(d[i]);
    e = a > e ? a : rC * e;
    env[i] = e;
  }
  const envPeak = peak;
  const envPeakAt = peakAt;

  let onset = 0;
  for (let i = 0; i < env.length; i++) if (env[i] > envPeak * 0.05) { onset = i; break; }
  const attackMs = ((envPeakAt - onset) / sr) * 1000;

  const t40 = envPeak * Math.pow(10, -40 / 20);
  const t60 = envPeak * Math.pow(10, -60 / 20);
  let decay40 = d.length, tail60 = 0;
  for (let i = envPeakAt; i < env.length; i++) if (env[i] < t40) { decay40 = i; break; }
  for (let i = env.length - 1; i >= 0; i--) if (env[i] > t60) { tail60 = i; break; }

  const { mag, binHz } = spectrum(buffer, 2048);
  let num = 0, den = 0;
  for (let k = 1; k < mag.length; k++) { num += k * binHz * mag[k]; den += mag[k]; }
  const centroid = den > 0 ? num / den : 0;

  let tailEnergy = 0;
  const tailStart = Math.min(d.length, Math.floor(0.3 * sr));
  for (let i = tailStart; i < d.length; i++) tailEnergy += d[i] * d[i];

  // RMS over the first 300 ms. Whole-buffer RMS is dominated by however much silence
  // and reverb tail the render window happens to include, which makes it useless for
  // comparing one sound's loudness against another's.
  let bodySq = 0;
  const bodyN = Math.min(d.length, Math.floor(0.3 * sr));
  for (let i = 0; i < bodyN; i++) bodySq += d[i] * d[i];
  const rms300 = Math.sqrt(bodySq / Math.max(1, bodyN));

  return {
    seconds: d.length / sr,
    peak,
    peakDb: 20 * Math.log10(Math.max(peak, 1e-9)),
    rms,
    rmsDb: 20 * Math.log10(Math.max(rms, 1e-9)),
    rms300,
    rms300Db: 20 * Math.log10(Math.max(rms300, 1e-9)),
    crestDb: 20 * Math.log10(Math.max(peak, 1e-9) / Math.max(rms300, 1e-9)),
    peakAtMs: (peakAt / sr) * 1000,
    attackMs,
    decay40Ms: ((decay40 - envPeakAt) / sr) * 1000,
    tail60Ms: (tail60 / sr) * 1000,
    tailRmsAfter300ms: Math.sqrt(tailEnergy / Math.max(1, d.length - tailStart)),
    dc: sum / d.length,
    lastSample: Math.abs(d[d.length - 1]),
    centroid,
    mag,
    binHz,
  };
}
